// store/uiSlice.ts
// ─────────────────────────────────────────────────────────────────────────
// Transient, in-memory UI state. NOTHING here is persisted to localStorage.
//
// The localStorage middleware only serializes `financial/*` actions, so this
// slice is deliberately kept OUT of the financial slice: a cross-tab handoff
// intent ("focus the Investments tab and prefill this contribution") must not
// survive a reload and resurrect a stale instruction. It lives only for the
// lifetime of the current session in the Redux store.
//
// Current job: the Money Flow → Investments handoff. When the user taps
// "Add a tracked investment" (or "Link to an existing investment") from the
// "Invest toward future" card, we stash the intent + the monthly contribution,
// switch tabs, and the Investments tab consumes it once, then clears it.
//
// Pure Redux Toolkit. No side effects, no thunks, no storage.
// ─────────────────────────────────────────────────────────────────────────

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// What the user was trying to do when they left Money Flow for Investments.
//   • 'add'  → open the add/search flow, carrying the monthly contribution so
//              the new tracked holding is pre-seeded and (on the user's intent)
//              linked to the Future investing plan afterwards.
//   • 'link' → focus the plan summary so the user can pick an existing holding
//              to link the plan to.
export type InvestmentHandoffIntent = 'add' | 'link';

export interface InvestmentHandoff {
	intent: InvestmentHandoffIntent;
	// The monthly contribution the user had entered on the Invest card, carried
	// so the Investments surface can prefill it. "" when none.
	monthlyContribution: string;
	// Human-readable source, always the Future investing plan for this pass.
	source: 'future-investing-plan';
}

export interface UiState {
	// Non-null only between "user tapped a destination in Money Flow" and
	// "Investments tab consumed it". Cleared immediately on consume.
	investmentHandoff: InvestmentHandoff | null;
}

const initialState: UiState = {
	investmentHandoff: null,
};

const uiSlice = createSlice({
	name: 'ui',
	initialState,
	reducers: {
		// Money Flow sets this, then switches the Dashboard tab to Investments.
		setInvestmentHandoff(
			state,
			action: PayloadAction<InvestmentHandoff>,
		) {
			state.investmentHandoff = action.payload;
		},
		// Investments consumes it exactly once (on mount / when it changes) and
		// clears it so a later manual tab switch doesn't re-trigger the flow.
		clearInvestmentHandoff(state) {
			state.investmentHandoff = null;
		},
	},
});

export const { setInvestmentHandoff, clearInvestmentHandoff } =
	uiSlice.actions;

export default uiSlice.reducer;
